/**
 * Saving a recipe's steps without stranding its stages. The stages point at
 * steps by number, and deleting a step renumbers the ones after it, so a save
 * is three writes that have to agree: read the stages, save the steps, then
 * move the stages onto the new numbers.
 */

import type { RecipeStage, RecipeStageInput } from "@/lib/api/requests/recipes";
import type { RecipeStep } from "@/components/recipes/execution-steps";
import { renumberStageSteps } from "@/lib/recipes/staged-method";

/**
 * Which write failed. `stages` means the steps were put back as they were, so
 * nothing changed; `stages-unreverted` means putting them back failed too, and
 * the saved steps are ahead of the stages.
 */
export type SaveStepsFailure = "load" | "steps" | "stages" | "stages-unreverted";

export class SaveStepsError extends Error {
  constructor(
    readonly failure: SaveStepsFailure,
    readonly cause: unknown,
  ) {
    super(`Saving the steps failed at ${failure}`);
    this.name = "SaveStepsError";
  }
}

interface SaveStepsOptions {
  steps: RecipeStep[];
  /** Each kept step's stored number to its new one. */
  renumbered: Map<number, number>;
  /** The steps as stored before this save, to put back if the stages can't follow. */
  previousSteps: unknown;
  loadStages: () => Promise<RecipeStage[] | undefined>;
  saveSteps: (steps: unknown) => Promise<unknown>;
  replaceStages: (stages: RecipeStageInput[]) => Promise<unknown>;
}

/**
 * Reads the stages first, so steps are never saved without knowing what
 * points at them. If the stages can't be moved onto the new numbers, the
 * steps are put back, so what is stored still agrees however the page is
 * left — a mapping kept only in the form would be lost on a reload.
 */
export async function saveStepsKeepingStages({
  steps,
  renumbered,
  previousSteps,
  loadStages,
  saveSteps,
  replaceStages,
}: SaveStepsOptions): Promise<void> {
  let stages: RecipeStage[] | undefined;
  try {
    stages = await loadStages();
  } catch (error) {
    throw new SaveStepsError("load", error);
  }

  try {
    await saveSteps(steps);
  } catch (error) {
    throw new SaveStepsError("steps", error);
  }

  const renumberedStages = stages ? renumberStageSteps(stages, renumbered) : null;
  if (!renumberedStages) return;

  try {
    await replaceStages(renumberedStages);
  } catch (error) {
    try {
      await saveSteps(previousSteps);
    } catch {
      throw new SaveStepsError("stages-unreverted", error);
    }
    throw new SaveStepsError("stages", error);
  }
}
